import React from "react";
import { Modal } from "react-bootstrap";
import { FaExclamationTriangle, FaTimes } from "react-icons/fa";

function FinalWarningModal({ show, warningCount, maxWarnings, onConfirm }) {
  const [countdown, setCountdown] = React.useState(5);
  const countdownRef = React.useRef(null);
  const onConfirmRef = React.useRef(onConfirm);

  React.useEffect(() => {
    onConfirmRef.current = onConfirm;
  }, [onConfirm]);

  React.useEffect(() => {
    if (!show) {
      return () => undefined;
    }

    console.log('[FinalWarningModal] Modal shown, starting countdown from 5');
    setCountdown(5);

    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }

    countdownRef.current = window.setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          if (countdownRef.current) {
            clearInterval(countdownRef.current);
          }
          console.log('[FinalWarningModal] Countdown reached 0, calling onConfirm');
          window.setTimeout(() => {
            console.log('[FinalWarningModal] Executing onConfirm callback');
            if (typeof onConfirmRef.current === 'function') {
              onConfirmRef.current();
            }
          }, 100);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      console.log('[FinalWarningModal] Cleanup: cleared countdown interval');
    };
  }, [show]);

  return (
    <Modal
      show={show}
      centered
      backdrop="static"
      keyboard={false}
      contentClassName="final-warning-modal"
    >
      <div style={{
        position: 'relative',
        borderRadius: 12,
        overflow: 'hidden',
        border: 'none',
      }}>
        {/* Red Header with Warning Icon */}
        <div style={{
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          padding: '40px 20px 60px',
          position: 'relative',
          textAlign: 'center',
        }}>
          {/* Animated Warning Icon */}
          <div style={{
            animation: 'shake 0.5s infinite',
          }}>
            <FaExclamationTriangle style={{ fontSize: 80, color: '#fff' }} />
          </div>
          
          {/* Wave Shape */}
          <svg
            style={{
              position: 'absolute',
              bottom: -1,
              left: 0,
              width: '100%',
              height: 40,
            }}
            viewBox="0 0 500 40"
            preserveAspectRatio="none"
          >
            <path
              d="M0,20 Q125,0 250,20 T500,20 L500,40 L0,40 Z"
              fill="#ffffff"
            />
          </svg>
        </div>

        {/* Content Area */}
        <div style={{
          background: '#fff',
          padding: '30px 40px 40px',
          textAlign: 'center',
        }}>
          {/* Title */}
          <h3 style={{
            fontSize: 28,
            fontWeight: 700,
            marginBottom: 8,
            color: '#1f2937',
          }}>
            Maximum Violations Reached
          </h3>

          {/* Warning Badge */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: '#fee2e2',
            color: '#dc2626',
            padding: '8px 16px',
            borderRadius: 20,
            fontSize: 14,
            fontWeight: 600,
            marginBottom: 20,
          }}>
            <FaTimes style={{ fontSize: 16 }} />
            {warningCount} of {maxWarnings} warnings
          </div>

          {/* Message */}
          <p style={{
            fontSize: 16,
            color: '#374151',
            marginBottom: 24,
            lineHeight: 1.7,
          }}>
            You have reached the maximum number of proctoring violations allowed for this assessment.
          </p>

          <p style={{
            fontSize: 18,
            fontWeight: 600,
            color: '#dc2626',
            marginBottom: 32,
            lineHeight: 1.6,
          }}>
            Your test will be automatically submitted and you will be exited from the assessment.
          </p>

          {/* Countdown Timer */}
          <div style={{
            background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
            padding: '20px',
            borderRadius: 12,
            marginBottom: 32,
            border: '2px solid #fca5a5',
          }}>
            <div style={{
              fontSize: 14,
              color: '#991b1b',
              fontWeight: 600,
              marginBottom: 8,
            }}>
              Auto-submitting in
            </div>
            <div style={{
              fontSize: 48,
              fontWeight: 700,
              color: '#dc2626',
              fontFamily: 'monospace',
              animation: countdown <= 3 ? 'pulse 0.5s ease-in-out infinite' : 'none',
              transition: 'all 0.3s ease',
            }}>
              {countdown}
            </div>
            <div style={{
              fontSize: 13,
              color: '#991b1b',
              marginTop: 4,
            }}>
              seconds
            </div>
          </div>

          {/* Button */}
          <button
            onClick={() => {
              if (countdownRef.current) {
                clearInterval(countdownRef.current);
                countdownRef.current = null;
              }
              if (typeof onConfirmRef.current === 'function') {
                onConfirmRef.current();
              }
            }}
            style={{
              padding: '14px 48px',
              fontSize: 16,
              fontWeight: 600,
              background: '#dc2626',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              transition: 'all 0.2s',
              minWidth: 200,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#b91c1c';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(220, 38, 38, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#dc2626';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            Acknowledge & Exit
          </button>
        </div>
      </div>

      {/* Custom CSS for modal and animations */}
      <style>{`
        .final-warning-modal {
          border: none;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
          border-radius: 12px;
        }
        .final-warning-modal .modal-content {
          border: none;
          border-radius: 12px;
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0) rotate(0deg); }
          25% { transform: translateX(-4px) rotate(-5deg); }
          75% { transform: translateX(4px) rotate(5deg); }
        }
        @keyframes pulse {
          0%, 100% { 
            transform: scale(1); 
            opacity: 1;
          }
          50% { 
            transform: scale(1.1); 
            opacity: 0.8;
          }
        }
      `}</style>
    </Modal>
  );
}

export default FinalWarningModal;

